import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import { showDialog } from "@widgets/CommonDialog";
import { fetchSavedAudioBlob, onMeetingAudioSeek } from "@shared/libs/meetingAudioStore";

export interface SnAudioValue {
  blob: Blob;
  file?: File;
  name: string;
  url: string;
  source: "record" | "upload";
}

interface SnAudioRecorderProps {
  value?: SnAudioValue | null;
  // rowKey: 이 음성이 붙을 행. 녹음은 시작한 행의 키로 넘긴다(녹음 중 행이 바뀌어도 시작한 행에 붙는다).
  onChange: (value: SnAudioValue | null, rowKey?: string | number) => void;
  disabled?: boolean;
  // 저장된 음성(js_attach의 dc_url)을 미리듣기용으로 불러올 때 사용한다.
  savedAudio?: { name: string; dcUrl: string } | null;
  // 행 식별자. 값이 바뀌면(=다른 행 선택) 이전 음성/재생 상태를 초기화하고, 녹음 중이면 녹음을 멈춘다.
  rowKey?: string | number;
}

const LEVEL_BAR_COUNT = 44;
// 녹음 형식 · 음질 — 말소리용(한 채널 · 32kbps). 전사는 서버가 어차피 32kbps 로 다시 줄인다(2026-10-22).
// 브라우저마다 되는 형식이 달라(Safari 는 mp4) 되는 것을 차례로 고른다.
const RECORD_BITRATE = 32000;
const RECORD_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];
const pickRecordMimeType = () => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  return RECORD_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};
const extensionOfMime = (mime: string) => {
  if (mime.startsWith("audio/mp4")) return "m4a";
  if (mime.startsWith("audio/ogg")) return "ogg";
  return "webm";
};
// 재생 배속 순환 목록(긴 회의 리뷰용).
const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

// 저장음성(dc_url) blob 캐시는 공용 모듈(meetingAudioStore)로 옮겨 재생기·전사가 공유한다.

// 저장음성(dc_url)별 마지막 재생위치(초) 캐시.
// 행 변경 등으로 컨트롤이 초기화됐다가 저장음성을 다시 로드할 때 듣던 지점부터 이어듣기용(세션 동안 유지).
const savedPositionCache = new Map<string, number>();

/**
 * Purpose: Format an audio time value as mm:ss for recording and playback UI.
 *
 * Usage:
 * const label = formatAudioTime(84);
 *
 * Main behavior:
 * Keeps the recorder display compact and stable while the timer updates.
 *
 * @param seconds audio position or duration in seconds
 * @returns formatted time text
 */
const formatAudioTime = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remains = Math.floor(safeSeconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remains
    .toString()
    .padStart(2, "0")}`;
};

/**
 * Purpose: Render a compact level meter for recording and playback progress.
 *
 * Usage:
 * renderLevelBars(levels, progress);
 *
 * Main behavior:
 * During recording the bars react to microphone input. During playback the same
 * track shows current progress.
 *
 * @param levels normalized level values between 0 and 1
 * @param progress normalized playback progress between 0 and 1
 * @returns level meter nodes
 */
const renderLevelBars = (levels: number[], progress = 0) =>
  levels.map((level, index) => {
    const active = progress > 0 && index / LEVEL_BAR_COUNT <= progress;
    return (
      <Box
        key={`${index}-${level}`}
        sx={{
          width: 3,
          height: `${Math.max(4, Math.round(level * 26))}px`,
          borderRadius: "3px",
          backgroundColor: active ? "#d7d7d7" : "#6f6f6f",
          opacity: active ? 1 : 0.75,
          transition: "height 80ms linear, background-color 120ms linear",
        }}
      />
    );
  });

/**
 * 목적: 브라우저 MediaRecorder 기반 음성 녹음과 음성 파일 선택/재생을 처리하는 공통 컴포넌트.
 *
 * 호출 예:
 * <SnAudioRecorder
 *   value={audio}
 *   onChange={setAudio}
 * />
 *
 * 주요 기능:
 * 녹음 시작/중지, audio/* 파일 업로드, 브라우저 객체 URL 기반 미리듣기, 선택 음성 삭제.
 * 녹음은 말소리용(한 채널 · 32kbps)으로 한다. onChange 는 음성이 붙을 행 키를 같이 넘긴다 —
 * 녹음 중 행이 바뀌면 녹음을 멈추고 시작한 행으로 넘긴다(2026-10-22).
 * 전사 원문 보기 칸(SnTranscriptView)에서 시각을 누르면 같은 행 키의 녹음기가 그 시각부터 재생한다(2026-10-23, meetingAudioStore 신호).
 *
 * 사용처:
 * 음성 파일을 서버 업로드하기 전 화면 상태로 보관하고, 이후 전사/STT 또는 파일 저장 API에 전달하는 화면.
 *
 * @param value 현재 선택 또는 녹음된 음성 정보
 * @param onChange 음성 정보 변경 이벤트
 * @param disabled 입력 비활성화 여부
 */
const SnAudioRecorder = ({
  value,
  onChange,
  disabled = false,
  savedAudio = null,
  rowKey,
}: SnAudioRecorderProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number>();
  const timerRef = useRef<number>();
  const recordingStartedAtRef = useRef<number>(0);
  // 일시정지 이전까지 누적된 녹음 경과(초). 일시정지/재개해도 타이머가 이어지도록 기준으로 쓴다.
  const pausedBaseRef = useRef<number>(0);
  // 저장음성을 lazy 다운로드한 뒤 자동 재생할지 표시(재생 버튼을 눌러 다운로드가 시작된 경우).
  const pendingPlayRef = useRef<boolean>(false);
  // 프로그레스바 seek용: 바 컨테이너 참조, 드래그(scrubbing) 중 여부, 로드 전 클릭한 이동 위치 보관.
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const scrubbingRef = useRef<boolean>(false);
  const pendingSeekRatioRef = useRef<number | null>(null);
  // 전사 원문 시각을 눌렀을 때 — 음성을 받기 전이면 받은 뒤 이 시각(초)부터 재생한다 (2026-10-23)
  const pendingSeekSecondsRef = useRef<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // 저장음성(dc_url)을 서버에서 받아오는 중인지 표시(재생/다운로드 버튼 스피너·비활성화용).
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  // 재생 배속(1x/1.25x/1.5x/2x).
  const [playbackRate, setPlaybackRate] = useState(1);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [localValue, setLocalValue] = useState<SnAudioValue | null>(
    value || null
  );
  const [levels, setLevels] = useState<number[]>(
    Array.from({ length: LEVEL_BAR_COUNT }, () => 0.18)
  );
  const audioValue = value || localValue;
  // 로드된 음성이 있거나, 저장음성(dc_url)이 있어 재생/다운로드가 가능한 상태.
  const hasAudio = Boolean(audioValue?.url || savedAudio?.dcUrl);
  // 녹음이 끝나는 시점(onstop)에 최신 onChange · 행 키를 쓰려고 ref 로 든다.
  // 예전에는 녹음 시작 때의 onChange 를 그대로 써서, 녹음 중 고친 입력이 멈출 때 시작 때 값으로 되돌아갔다.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const rowKeyRef = useRef(rowKey);
  rowKeyRef.current = rowKey;
  // 녹음을 시작한 행. 녹음은 이 행에 붙는다.
  const recordingRowKeyRef = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    if (value) setLocalValue(value);
  }, [value]);

  // 행이 바뀌면(rowKey 변경) 로드된 음성을 비운다. 나머지 재생상태 초기화는 아래 url 변경 effect가 처리한다.
  // 저장음성 실제 다운로드는 재생/다운로드 클릭 시점에만 한다(포커스마다 받지 않음).
  // 녹음 중이면 멈춘다 — onstop 이 녹음을 시작한 행으로 넘긴다(그리드에 행 이동을 막는 기능이 없다).
  useEffect(() => {
    pendingPlayRef.current = false;
    pendingSeekSecondsRef.current = null;
    setLocalValue(null);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);

  // 재생 소스(url)가 바뀌면 이전 재생을 멈추고 새 소스를 다시 로드한다.
  // src 속성만 바뀌면 브라우저가 이전 오디오를 계속 물고 있어, 행 전환 시 다른 음성이 재생되는 문제를 막는다.
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    audioRef.current?.load();
  }, [audioValue?.url]);

  // 화면을 닫을 때만 마이크 · 미터 · 타이머를 푼다.
  // 음성 URL 은 여기서 풀지 않는다 — 저장 전 음성은 화면 보관소가 행마다 들고 있다가 다시 보여 주므로,
  // 행을 옮길 때 풀면 돌아와서 재생이 안 된다. 보관소가 지울 때 푼다(meetingAudioStore).
  useEffect(() => {
    return () => {
      stopMeter();
      stopTimer();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 배속 변경 또는 새 음성 로드 시 오디오 요소에 배속을 반영한다.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate, audioValue?.url]);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const stopMeter = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
    audioContextRef.current?.close();
    audioContextRef.current = null;
    analyserRef.current = null;
  };

  // 시작과 재개 양쪽에서 쓴다. 누적 경과(pausedBaseRef)에 현재 구간 경과를 더해 표시한다.
  const startTimer = () => {
    recordingStartedAtRef.current = Date.now();
    stopTimer();
    timerRef.current = window.setInterval(() => {
      setRecordingSeconds(
        pausedBaseRef.current + (Date.now() - recordingStartedAtRef.current) / 1000
      );
    }, 250);
  };

  const startMeter = (stream: MediaStream) => {
    stopMeter();

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const update = () => {
      analyser.getByteFrequencyData(data);
      const chunkSize = Math.max(1, Math.floor(data.length / LEVEL_BAR_COUNT));
      const nextLevels = Array.from({ length: LEVEL_BAR_COUNT }, (_, index) => {
        const start = index * chunkSize;
        const chunk = data.slice(start, start + chunkSize);
        const average =
          chunk.reduce((sum, item) => sum + item, 0) /
          Math.max(1, chunk.length);
        return Math.min(1, Math.max(0.12, average / 160));
      });

      setLevels(nextLevels);
      animationRef.current = requestAnimationFrame(update);
    };

    update();
  };

  const startRecording = async () => {
    if (disabled || isRecording) return;

    // 마이크 접근(navigator.mediaDevices)은 보안 컨텍스트(HTTPS 또는 localhost)에서만 제공된다.
    // 평문 HTTP + LAN IP 접속에서는 undefined라, 크래시 대신 안내 후 업로드를 유도한다.
    if (!navigator.mediaDevices?.getUserMedia) {
      await showDialog(
        "alter",
        "녹음은 보안 연결(HTTPS) 접속에서만 가능합니다. 음성 파일 업로드를 이용해 주세요."
      );
      return;
    }

    let stream: MediaStream;
    try {
      // 말소리면 한 채널로 충분하다. 반향 · 잡음 줄이기는 브라우저 기본값(켜짐)을 그대로 쓴다.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1 },
      });
    } catch (error) {
      await showDialog(
        "alter",
        "마이크 권한이 필요합니다. 브라우저에서 마이크 사용을 허용해 주세요."
      );
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];

    const pickedMimeType = pickRecordMimeType();
    const recorder = new MediaRecorder(stream, {
      ...(pickedMimeType ? { mimeType: pickedMimeType } : {}),
      audioBitsPerSecond: RECORD_BITRATE,
    });
    recorderRef.current = recorder;
    recordingRowKeyRef.current = rowKeyRef.current;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      // 실제로 녹음된 형식으로 이름을 붙인다(Safari 는 mp4 인데 webm 이라 붙였었다).
      const mime = (recorder.mimeType || pickedMimeType || "audio/webm").split(";")[0];
      const blob = new Blob(chunksRef.current, { type: mime });
      const url = URL.createObjectURL(blob);
      const name = `meeting-${Date.now()}.${extensionOfMime(mime)}`;

      // 등록 화면에서는 음성 파일을 서버에 올리기 전까지 브라우저 객체로만 보관한다.
      const nextValue = {
        blob,
        file: new File([blob], name, { type: mime }),
        name,
        url,
        source: "record",
      } as SnAudioValue;

      // 녹음 중에 행이 바뀌었으면 지금 행에는 보이지 않고, 녹음을 시작한 행에 붙는다.
      const startedRowKey = recordingRowKeyRef.current;
      if (startedRowKey === rowKeyRef.current) setLocalValue(nextValue);
      onChangeRef.current(nextValue, startedRowKey);

      setIsRecording(false);
      setIsPaused(false);
      stopTimer();
      stopMeter();
      stopStream();
    };

    recorder.start();
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsRecording(true);
    setIsPaused(false);
    pausedBaseRef.current = 0;
    setRecordingSeconds(0);
    startTimer();
    startMeter(stream);
  };

  const pauseRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    recorder.pause();
    // 현재 구간 경과를 누적에 더하고 타이머/미터를 멈춰 화면을 고정한다.
    pausedBaseRef.current += (Date.now() - recordingStartedAtRef.current) / 1000;
    setRecordingSeconds(pausedBaseRef.current);
    stopTimer();
    stopMeter();
    setIsPaused(true);
  };

  const resumeRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") return;

    recorder.resume();
    setIsPaused(false);
    startTimer();
    if (streamRef.current) startMeter(streamRef.current);
  };

  const stopRecording = () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") {
      return;
    }

    recorderRef.current.stop();
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    const nextValue = {
      blob: file,
      file,
      name: file.name,
      url,
      source: "upload",
    } as SnAudioValue;

    setLocalValue(nextValue);
    onChangeRef.current(nextValue, rowKeyRef.current);

    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    event.target.value = "";
  };

  const clearAudio = () => {
    if (audioValue?.url) URL.revokeObjectURL(audioValue.url);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setLocalValue(null);
    onChangeRef.current(null, rowKeyRef.current);
  };

  // 저장음성(dc_url)을 서버에서 받아온다. 재생/다운로드 클릭 시점에만 호출한다.
  // 같은 dc_url은 캐시에서 재사용하고, 진행 중 요청은 합쳐 한 번만 서버에 요청한다.
  // 저장음성 다운로드+캐시는 공용 모듈에 위임한다(재생기·전사가 같은 캐시 공유 → 이미 받은 건 재사용).
  const fetchSavedBlob = async (): Promise<Blob | null> => {
    return fetchSavedAudioBlob(savedAudio?.dcUrl || "");
  };

  // 저장음성을 받아 localValue에 담아 재생 가능한 상태로 만든다(한번 받으면 캐시되어 재다운로드 안 함).
  const loadSavedAudio = async (): Promise<SnAudioValue | null> => {
    setIsLoadingSaved(true);
    try {
      const blob = await fetchSavedBlob();
      if (!blob) return null;
      const nextValue: SnAudioValue = {
        blob,
        name: savedAudio?.name || "audio",
        url: URL.createObjectURL(blob),
        source: "upload",
      };
      setLocalValue(nextValue);
      return nextValue;
    } finally {
      setIsLoadingSaved(false);
    }
  };

  const triggerDownload = (url: string, name: string) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    anchor.click();
  };

  const togglePlayback = async () => {
    if (isRecording) return;

    // 아직 다운로드 안 된 저장음성이면 이 시점에 받아서(onCanPlay에서) 재생한다.
    if (!audioValue?.url) {
      if (savedAudio?.dcUrl) {
        pendingPlayRef.current = true;
        await loadSavedAudio();
      }
      return;
    }

    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      await audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const downloadAudio = async () => {
    if (audioValue?.url) {
      triggerDownload(audioValue.url, audioValue.name);
      return;
    }
    // 저장음성은 다운로드 클릭 시점에 받아 내려준다.
    const loaded = await loadSavedAudio();
    if (loaded) {
      triggerDownload(loaded.url, loaded.name);
    }
  };

  // 프로그레스바 위 포인터 x좌표를 0~1 비율로 환산한다.
  const ratioFromClientX = (clientX: number) => {
    const el = progressBarRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  // 주어진 비율 위치로 재생 지점을 이동한다(전체 blob이 로컬에 있어 즉시 이동).
  const seekToRatio = (ratio: number) => {
    const audio = audioRef.current;
    if (!audio || !(audio.duration > 0)) return;
    const t = Math.min(1, Math.max(0, ratio)) * audio.duration;
    audio.currentTime = t;
    setCurrentTime(t);
  };

  // 현재 위치에서 지정 초만큼 앞/뒤로 이동한다(구간 점프).
  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio || !(audio.duration > 0)) return;
    const t = Math.min(audio.duration, Math.max(0, audio.currentTime + seconds));
    audio.currentTime = t;
    setCurrentTime(t);
  };

  // 재생 배속을 다음 단계로 순환한다(1x→1.25x→1.5x→2x→1x).
  const cycleSpeed = () => {
    const index = PLAYBACK_RATES.indexOf(playbackRate);
    const next = PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length];
    setPlaybackRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  /**
   * 전사 원문의 시각을 눌렀을 때 — 그 시각(초)으로 가서 재생한다.
   * 음성이 이미 올라와 있으면 바로, 저장음성을 아직 안 받았으면 받은 뒤(onLoadedMetadata · onCanPlay) 그 시각부터.
   */
  const playFrom = async (seconds: number) => {
    if (isRecording) return;
    const audio = audioRef.current;
    if (audioValue?.url && audio && audio.readyState >= 1) {
      audio.currentTime = seconds;
      setCurrentTime(seconds);
      await audio.play();
      setIsPlaying(true);
      return;
    }
    pendingSeekSecondsRef.current = seconds;
    pendingPlayRef.current = true;
    if (!audioValue?.url && savedAudio?.dcUrl) {
      await loadSavedAudio();
    }
  };
  const playFromRef = useRef(playFrom);
  playFromRef.current = playFrom;

  // 같은 행의 전사 원문 보기 칸이 보내는 '이 시점 재생'
  useEffect(
    () =>
      onMeetingAudioSeek((key, seconds) => {
        if (key !== String(rowKeyRef.current ?? "")) return;
        playFromRef.current(seconds);
      }),
    []
  );

  // 메타데이터 로드 직후 시작 위치를 정한다.
  // 0) 전사 원문 시각을 눌러 로드된 경우 그 시각으로, 1) 바를 클릭해 로드된 경우 그 위치로, 2) 저장음성 이어듣기면 캐시된 위치로 이동한다.
  const applyInitialPosition = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (pendingSeekSecondsRef.current != null) {
      // 길이를 모르는 녹음(webm)이어도 시각으로는 옮길 수 있다
      const t = Math.max(0, pendingSeekSecondsRef.current);
      pendingSeekSecondsRef.current = null;
      audio.currentTime = t;
      setCurrentTime(t);
      return;
    }
    const dur = audio.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;

    if (pendingSeekRatioRef.current != null) {
      const t = Math.min(1, Math.max(0, pendingSeekRatioRef.current)) * dur;
      pendingSeekRatioRef.current = null;
      audio.currentTime = t;
      setCurrentTime(t);
      return;
    }

    const dcUrl = savedAudio?.dcUrl;
    if (dcUrl) {
      const saved = savedPositionCache.get(dcUrl);
      if (saved != null && saved > 0 && saved < dur) {
        audio.currentTime = saved;
        setCurrentTime(saved);
      }
    }
  };

  // 저장음성이면 마지막 재생위치를 캐시에 기록한다(이어듣기용).
  const rememberPosition = (time: number) => {
    const dcUrl = savedAudio?.dcUrl;
    if (dcUrl) savedPositionCache.set(dcUrl, time);
  };

  const handleBarPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isRecording) return;
    const ratio = ratioFromClientX(e.clientX);
    // 아직 안 받은 저장음성이면 받아온 뒤 그 위치로 이동한다(자동재생은 하지 않음).
    if (!audioValue?.url) {
      if (savedAudio?.dcUrl) {
        pendingSeekRatioRef.current = ratio;
        loadSavedAudio();
      }
      return;
    }
    scrubbingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekToRatio(ratio);
  };

  const handleBarPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbingRef.current) return;
    seekToRatio(ratioFromClientX(e.clientX));
  };

  const handleBarPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (error) {
      // 캡처 해제 실패는 무시한다.
    }
  };

  // 플레이어에 포커스가 있을 때만 동작하는 단축키(폼 입력과 충돌 방지).
  // Space: 재생/정지, ←/→: 10초 이동, Shift+←/→: 30초 이동.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isRecording || !hasAudio) return;
    if (e.key === " " || e.code === "Space") {
      e.preventDefault();
      togglePlayback();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      skip(e.shiftKey ? -30 : -10);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      skip(e.shiftKey ? 30 : 10);
    }
  };

  const playbackProgress =
    duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const displayTime = isRecording
    ? formatAudioTime(recordingSeconds)
    : `${formatAudioTime(currentTime)}/${formatAudioTime(duration)}`;

  return (
    <Box
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        border: "1px solid #babfc7",
        padding: "5px 8px",
        minHeight: "42px",
        width: "100%",
        backgroundColor: isRecording ? "#171717" : "#f8f8f8",
        color: isRecording ? "#f3f3f3" : "#222",
        overflow: "hidden",
        outline: "none",
      }}
    >
      <Box
        ref={progressBarRef}
        onPointerDown={handleBarPointerDown}
        onPointerMove={handleBarPointerMove}
        onPointerUp={handleBarPointerUp}
        onPointerCancel={handleBarPointerUp}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isRecording ? "2px" : 0,
          flex: "1 1 auto",
          minWidth: 120,
          height: 30,
          // 녹음 중이 아니고 재생 가능한 음성이 있으면 클릭/드래그로 이동 가능함을 표시.
          cursor: hasAudio && !isRecording ? "pointer" : "default",
          touchAction: "none",
        }}
      >
        {isRecording ? (
          // 녹음 중: 마이크 입력 레벨 파형.
          renderLevelBars(levels, 0)
        ) : (
          // 재생: 진행 슬라이더(채워지는 트랙 + 핸들). 드래그/클릭 seek는 상위 Box가 처리.
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 6,
              borderRadius: 3,
              backgroundColor: "#d5d8dd",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                height: "100%",
                width: `${playbackProgress * 100}%`,
                borderRadius: 3,
                backgroundColor: "#1976d2",
              }}
            />
            {hasAudio && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: `${playbackProgress * 100}%`,
                  transform: "translate(-50%, -50%)",
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          minWidth: 72,
          color: isRecording ? "#f3f3f3" : "#333",
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {displayTime}
      </Typography>

      {hasAudio && !isRecording && (
        <>
          <Tooltip title="10초 뒤로" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => skip(-10)}
                sx={{ color: "#333" }}
              >
                <Replay10Icon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              isLoadingSaved ? "불러오는 중" : isPlaying ? "일시정지" : "재생"
            }
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={togglePlayback}
                disabled={isLoadingSaved}
                sx={{ color: "#333" }}
              >
                {isLoadingSaved ? (
                  <CircularProgress size={16} sx={{ color: "#333" }} />
                ) : isPlaying ? (
                  <PauseIcon />
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="10초 앞으로" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => skip(10)}
                sx={{ color: "#333" }}
              >
                <Forward10Icon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="재생 속도 (Shift+←/→: 30초 이동)" arrow>
            <span>
              <Button
                size="small"
                onClick={cycleSpeed}
                sx={{
                  minWidth: 42,
                  px: 0.5,
                  color: "#333",
                  fontWeight: "bold",
                }}
              >
                {playbackRate}x
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {isRecording && (
        <Tooltip title={isPaused ? "녹음 재개" : "일시정지"} arrow>
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={isPaused ? resumeRecording : pauseRecording}
              sx={{ color: "#f3f3f3" }}
            >
              {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Tooltip title={isRecording ? "녹음 정지" : "녹음 시작"} arrow>
        <span>
          <Button
            size="small"
            variant={isRecording ? "contained" : "outlined"}
            color={isRecording ? "error" : "primary"}
            startIcon={isRecording ? <StopIcon /> : <MicIcon />}
            disabled={disabled}
            onClick={isRecording ? stopRecording : startRecording}
            sx={{
              minWidth: 34,
              width: 34,
              px: 0,
              fontSize: 0,
              "& .MuiButton-startIcon": { m: 0 },
            }}
          >
            {isRecording ? "녹음중지" : "녹음"}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="음성 파일 업로드" arrow>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={disabled || isRecording}
            onClick={() => inputRef.current?.click()}
            sx={{
              minWidth: 34,
              width: 34,
              px: 0,
              fontSize: 0,
              "& .MuiButton-startIcon": { m: 0 },
            }}
          >
            업로드
          </Button>
        </span>
      </Tooltip>
      {hasAudio && !isRecording && (
        <Tooltip title={isLoadingSaved ? "불러오는 중" : "다운로드"} arrow>
          <span>
            <IconButton
              size="small"
              onClick={downloadAudio}
              disabled={isLoadingSaved}
            >
              {isLoadingSaved ? (
                <CircularProgress size={16} sx={{ color: "#333" }} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {audioValue?.url && !isRecording && (
        <Tooltip title="삭제" arrow>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={disabled}
              onClick={clearAudio}
              sx={{ fontSize: 0 }}
            >
              <DeleteOutlineIcon fontSize="small" />
              삭제
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!hasAudio && !isRecording && (
        <GraphicEqIcon sx={{ color: "#8a8a8a", fontSize: 20 }} />
      )}
      <audio
        ref={audioRef}
        src={audioValue?.url || undefined}
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration);
          // 로드 직후 시작 위치 결정(바 클릭 위치 또는 이어듣기 위치).
          applyInitialPosition();
        }}
        onCanPlay={() => {
          // 저장음성을 lazy 다운로드한 뒤 재생 대기 중이면 로드 완료 시 자동 재생한다.
          if (pendingPlayRef.current) {
            pendingPlayRef.current = false;
            audioRef.current?.play();
            setIsPlaying(true);
          }
        }}
        onTimeUpdate={(event) => {
          const t = event.currentTarget.currentTime;
          setCurrentTime(t);
          // 저장음성이면 마지막 위치를 캐시에 기록해 이어듣기에 쓴다.
          rememberPosition(t);
        }}
        onEnded={() => {
          setIsPlaying(false);
          // 끝까지 재생하면 다음엔 처음부터 재생하도록 위치를 초기화한다.
          const dcUrl = savedAudio?.dcUrl;
          if (dcUrl) savedPositionCache.delete(dcUrl);
          if (audioRef.current) audioRef.current.currentTime = 0;
          setCurrentTime(0);
        }}
        style={{ display: "none" }}
      />
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        onChange={handleUpload}
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default React.memo(SnAudioRecorder);
